<template>
  <div class="aco-grid">
    <div class="aco-info-box">
      <b-card>
        <margin-fields :fields="marginFields()"/>
        <b-button block v-on:click="showLicenseUsages()">{{ $t('showLicenseUsages') }}</b-button>
        <b-button block v-on:click="productSearch()">{{ $t('productSearch') }}</b-button>
        <b-button block v-on:click="showOwner()">{{ $t('showOwner') }}</b-button>
      </b-card>
    </div>

    <div>
      <div class="aco-details-box">
        <b-col class="aco-details">
          <details-form :fields="productFields()" :state-object="license"/>
        </b-col>
      </div>

      <div class="aco-details-box">
        <b-col class="aco-details">
          <details-form :fields="infoFields()" :state-object="license"/>
          <details-form :fields="typeFields()" :state-object="license"/>
          <b-row>
            <b-col md="12">
              <b-button type="button" class="float-left mt-3" @click="getLicense">{{ $t('reset') }}</b-button>
              <b-button type="button" variant="primary" class="float-right mt-3" @click="updateLicense()">{{ $t('save') }}</b-button>
            </b-col>
          </b-row>
        </b-col>
      </div>

      <div class="aco-details-box">
        <b-col class="aco-details">
          <details-form v-if="license.ownership === 'ORGANIZATION'" :fields="organizationOwnerFields()" :state-object="license"/>
          <details-form v-if="license.ownership === 'PRIVATE'" :fields="privateOwnerFields()" :state-object="license"/>
          <details-form v-if="license.ownership === 'ALL'" :fields="allOwnerFields()" :state-object="license"/>
        </b-col>
      </div>
    </div>

    <b-modal id="showLicenseUsages" ref="showLicenseUsages" modal-class="modal-fullscreen" hide-footer size="xl">
      <template slot="modal-title">{{ $t('licenseUsage') }}</template>
      <license-usages :license-id="license.licenseId"/>
    </b-modal>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import BaseComponent from '../../components/BaseComponent'
  import DetailsForm from '../../components/DetailsForm'
  import MarginFields from '../../components/MarginFields'
  import LicenseUsages from './LicenseUsages'

  export default {
    name: 'LicenseDetails',
    extends: BaseComponent,
    components: {
      LicenseUsages,
      'detailsForm': DetailsForm,
      'marginFields': MarginFields
    },
    mounted () {
      this.getLicense()
    },
    computed: {
      ...mapGetters('licenses', ['license']),
    },
    methods: {
      marginFields () {
        return [
          { name: 'orderId', displayName: this.$t('orderId'), value: this.license.orderId },
          { name: 'created', displayName: this.$t('created'), value: this.formatTimestamp(this.license.created) },
          { name: 'createdBy', displayName: this.$t('createdBy'), value: this.license.createdBy },
          { name: 'modified', displayName: this.$t('modified'), value: this.formatTimestamp(this.license.modified) },
          { name: 'modifiedBy', displayName: this.$t('modifiedBy'), value: this.license.createdBy }
        ]
      },
      infoFields () {
        return [
          { type: 'header', width: 12, displayName: this.$t('licenseInformation') },
          { type: 'dropdown', readonly: false, width: 3, name: 'licenseModel', items: this.licenseModels },
          { type: 'dropdown', readonly: false, name: 'status', width: 3, items: this.licenseStatuses },
          { type: 'date', readonly: false, name: 'validFrom', width: 3 },
          { type: 'date', readonly: false, name: 'validTo', width: 3 },
          { type: 'textarea', readonly: false, name: 'description', width: 12, displayName: this.$t('comment') }
        ]
      },
      typeFields () {
        return [
          { type: 'header', backButton: false, width: 12, displayName: this.$t('licenseProperties') },
          { type: 'dropdown', readonly: true, name: 'licenseType', width: 3, items: this.licenseTypes },
          { type: 'text', readonly: this.isLicenseTypeUnEnforced(this.license.licenseType), name: 'usageRights', width: 3 },
          { type: 'dropdown', name: 'productType', readonly: true, width: 3, items: this.productTypes },
        ]
      },
      organizationOwnerFields () {
        return [
          { type: 'header', width: 12, backButton: false, displayName: this.$t('owner') },
          { type: 'dropdown', readonly: true, name: 'ownership', width: 3, items: this.licenseOwnershipTypes, displayName: this.$t('type') },
          { type: 'text', readonly: true, width: 3, name: 'ownerOrganizationName', displayName: this.$t('organizationName') },
          { type: 'text', readonly: true, width: 3, name: 'ownerOrganizationNumber', displayName: this.$t('organizationNumber') }
        ]
      },
      privateOwnerFields () {
        return [
          { type: 'header', width: 12, backButton: false, displayName: this.$t('owner') },
          { type: 'dropdown', readonly: true, name: 'ownership', width: 3, items: this.licenseOwnershipTypes, displayName: this.$t('type') },
          { type: 'text', readonly: true, name: 'ownerFullName', width: 3, displayName: this.$t('fullName') },
          { type: 'text', readonly: true, name: 'ownerUserId', width: 3, displayName: this.$t('userId') }
        ]
      },
      allOwnerFields () {
        return [
          { type: 'header', width: 12, backButton: false, displayName: this.$t('owner') },
          { type: 'dropdown', readonly: true, name: 'ownership', width: 3, items: this.licenseOwnershipTypes, displayName: this.$t('type') },
        ]
      },
      productFields () {
        return [
          { type: 'header', backButton: true, width: 12, displayName: this.$t('productInformation') },
          { type: 'text', readonly: true, name: 'ean', width: 3, displayName: this.$t('ean_isbn') },
          { type: 'text', readonly: true, name: 'productName', width: 3, displayName: this.$t('name') },
          { type: 'dropdown', readonly: true, name: 'productType', width: 3, items: this.productTypes },
          { type: 'boolean', readonly: true, name: 'isBundle', displayName: this.$t('bundle') }
        ]
      },
      getLicense () {
        this.$store.dispatch('licenses/clearLicense')
        this.$store.dispatch('licenses/getLicense', this.$route.params.id)
      },
      updateLicense () {
        this.$store.dispatch('licenses/updateLicense', this.$route.params.id)
      },
      showLicenseUsages () {
        this.$root.$emit('bv::show::modal', 'showLicenseUsages')
      },
      productSearch () {
        this.$store.dispatch('products/resetFilter')
        let payload = {
          ean: this.license.ean,
          source: null
        }
        this.$store.dispatch('products/updateEanAndSource', payload)
          .then(() => {
            this.$router.push('/products')
          })
      },
      showOwner () {
        let ownerShip = this.license.ownership
        if (ownerShip === 'PRIVATE') {
          this.$router.push('/users/' + this.license.ownerUserId)
        }
        if (ownerShip === 'ORGANIZATION') {
          this.$router.push('/organizations/' + this.license.ownerOrganizationNumber)
        }
      }
    },
    i18n: {
      messages: {
        en: {
          'productInformation': 'Product information',
          'licenseUsage': 'License usage',
          'showLicenseUsages': 'Show license usages',
          'comment': 'Comment'
        },
        nb: {
          'productInformation': 'Produktinformasjon',
          'licenseUsage': 'Lisensbruk',
          'showLicenseUsages': 'Vis lisensbruk',
          'comment': 'Kommentar'
        }
      }
    }
  }
</script>
